import * as React from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import * as api from "app/shared/api";
import {
  Row,
  Col,
  Affix,
  Select,
  Typography,
  Alert,
  Space,
  Dropdown,
  Button,
  Switch,
  Tag,
  message,
  Modal,
} from "antd";
import "antd/dist/reset.css";
import { Centre, Product, PricingYearId, School, pickDefaultPricingYear } from "app/shared/model";
import {
  getPricingYearsByDivision,
  DivisionPricingYear,
  isDraftPricingYear,
  toPricingYearId,
} from "app/shared/api/getYears";
import {
  getPricingYears,
  getSelectedCentre,
  getCentres,
  getProducts,
  getSelectedProductType,
  getSelectedPricingYear,
  getFormState,
  getPricelist,
  getDraftPricelist,
  getFormErrors,
  getIsLoading,
  getSchools,
} from "../store/selectors";
import { connect } from "react-redux";
import "./MainContent.css";
import ProductTable, { ProductTableHandle } from "./ProductTable";
import FeePricelistTable, {
  FeePricelistTableHandle,
} from "./FeePricelistTable";
import PricelistFooter from "./layout/PricelistFooter";
import AddProductOfferingModal from "./AddProductOfferingModal";
import AddCurrencyModal from "./AddCurrencyModal";
import {
  changeSelectedPricingYear,
  changeSelectedCentre,
  loadPricelist,
  loadDraftPricelist,
  updateDraftPricelistItem,
  setFormState,
  setIsLoading,
  setFormErrors,
} from "../store/actions";
import {
  DateStrategy,
  IsoDayOfWeek,
} from "app/shared/model/EditProductOffering";
import TableActionButtons from "app/shared/components/Table/TableActionButtons";
import UnsavedChangesModal from "app/shared/ui/UnsavedChangesModal";
import useUnsavedChangesPrompt from "app/shared/hooks/useUnsavedChangesPrompt";
import { EllipsisOutlined } from "@ant-design/icons";
import DateHelper, { ManipulationTypes } from "app/helpers/dateHelper";
import { SaveState } from "app/shared/ui/GenericSaveAndBackButton";
import {
  DateStrategyAssign,
  EditPricelistResponse,
  OfferingType,
  FeeOfferingDto,
  SchoolOfferingStatus,
  UpsertOfferingsRequest,
  StandardOfferingDto,
  PackageOfferingDto,
  PricePeriods,
  PriceBands,
} from "app/shared/model/EditPricelist";
import { FormError } from "app/shared/model/Form";
import {
  formatPricelistApiErrors,
  getPricelistValidationSaveMessage,
  mapPricelistApiErrorMessage,
} from "../domain/validation/pricelistErrorMessages";
import { validateOfferingWorkingWeeksDateStrategies } from "../domain/dateStrategy/workingWeeksStartDate";
import { CurrentUser, UserPermissions } from "app/shared/model/CurrentUser";
import { getCurrentUser } from "app/shared/currentUser";
import {
  normIso,
  getCentreCurrencyIso,
} from "../domain/currency/normalizeCurrency";
import { isFeeOffering, isNonFeeOffering } from "../domain/offering/guards";
import {
  getDivisionId,
  readSelectedCentreCode,
  toSchoolIdNumber,
} from "../domain/storage/localStorage";
import { PRICELIST_PAGE_READY } from "app/shared/division/pricelistPageEvents";
import { useCurrencies } from "../hooks/useCurrencies";
import {
  syncCurrenciesInAllBands,
  getEnabledCurrencies,
} from "../domain/currency/enabledCurrencies";
import { useUnsavedChangesDialog } from "../hooks/useUnsavedChangesDialog";
import { buildNonFeeOfferingKey, offeringKey } from "../domain/offering/keys";
import {
  isSameOfferingForUpdate,
  prepareOfferingForApi,
} from "../domain/offering/saveMapping";
import {
  PublishedEventType,
  StatusIcon,
  useSignalREvent,
} from "app/helpers/signalRHelper";
import {
  fullyValidateDraftPricingYear,
  publishDraftPricingYear,
  loadDraftOfferings,
  saveDraftPricelistContent,
  getDraftMinimumStartDates,
  DraftValidationResult,
  type DraftMinimumStartDatesByProduct,
} from "app/shared/api/draftPricingYear";
import {
  getPricingYearWindow,
  resolveProductAllowedStartDate,
} from "app/pages/PricelistNew/domain/pricingYear/window";
import { resolveActivePricingYear } from "app/pages/PricelistNew/domain/pricingYear/resolveActivePricingYear";
import { toIso } from "../domain/date/iso";
import { formatRecurranceStartDaysLabel } from "app/pages/PricelistNew/domain/dateStrategy/recurranceDays";
import { isAnyUnits } from "app/pages/PricelistNew/domain/priceBands/priceBandsNormalize";
import { EventType } from "@azure/msal-browser";

export interface MainContentProps {
  isDraftMode?: boolean;
}

const { Title, Text } = Typography;
const { Option } = Select;

/** On /pricelist, default year must not be a draft pricing year. */
function pickPublishedDefaultPricingYear(
  years: PricingYearId[],
  draftPricingYearIds: Set<number>
): PricingYearId | undefined {
  const published = years.filter((py) => !draftPricingYearIds.has(py.id));
  return pickDefaultPricingYear(published.length ? published : years);
}

interface StateProps {
  schools: School[];
  centres: Centre[];
  products: Product[];
  pricingYears: PricingYearId[];

  pricelist?: EditPricelistResponse[];
  draftPricelist?: EditPricelistResponse[];

  selectedCentreCode?: string;
  selectedProductType: string;
  selectedPricingYear?: number;

  formState: SaveState;
  formErrors: FormError[];
  isLoading: boolean;

  user: CurrentUser;
}

interface DispatchProps {
  changeSelectedPricingYear: (value: number) => void;
  changeSelectedCentre: (centreCode: string) => void;
  loadPricelist: (pricelist: EditPricelistResponse[]) => void;
  loadDraftPricelist: (draftPricelist: EditPricelistResponse[]) => void;
  updateDraftPricelistItem: (
    pricelistItem: EditPricelistResponse,
    pricelistIndex: number
  ) => void;
  setFormState: (formState: SaveState, errorMessage?: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setFormErrors: (formErrors: FormError[]) => void;
}

type InternalProps = StateProps & DispatchProps & MainContentProps;
const MainContentView = (props: InternalProps) => <MainContent {...props} />;

function MainContent(props: InternalProps) {
  const {
    schools,
    centres,
    selectedCentreCode,
    selectedProductType,
    selectedPricingYear,
    formState,
    pricelist,
    draftPricelist,
    changeSelectedPricingYear,
    loadPricelist,
    loadDraftPricelist,
    updateDraftPricelistItem,
    setFormState,
    setIsLoading,
    setFormErrors,
  } = props;

  const draftPricelistRef = React.useRef(draftPricelist);
  const reloadRequestIdRef = React.useRef(0);
  const lastAutoReloadKeyRef = React.useRef<string | null>(null);
  const userPickedPricingYearRef = React.useRef(false);
  const syncedDraftNavYearRef = React.useRef<number | null | undefined>(undefined);
  React.useEffect(() => {
    draftPricelistRef.current = draftPricelist;
  }, [draftPricelist]);

  const [editMode, setEditMode] = React.useState<boolean>(false);
  const feeTableRef = React.useRef<FeePricelistTableHandle | null>(null);
  const productTableRef = React.useRef<ProductTableHandle | null>(null);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [divisionId, setDivisionId] = React.useState<number>(getDivisionId());
  const [divisionPricingYears, setDivisionPricingYears] = React.useState<
    PricingYearId[]
  >(props.pricingYears ?? []);
  const [addOfferingModalVisible, setAddOfferingModalVisible] =
    React.useState<boolean>(false);
  const [addCurrencyModalVisible, setAddCurrencyModalVisible] =
    React.useState<boolean>(false);
  const [selectedSchoolId, setSelectedSchoolId] = React.useState<
    number | undefined
  >(undefined);
  const [showInactiveOnly, setShowInactiveOnly] = React.useState<boolean>(false);
  const [pendingSchoolOps, setPendingSchoolOps] = React.useState<
    {
      op: "create" | "recommission" | "decommission";
      productIdText: string;
      type: OfferingType;
      pricingYear: number;
      schoolId: number;
    }[]
  >([]);
  const [lastDraftValidation, setLastDraftValidation] =
    React.useState<DraftValidationResult | null>(null);
  const [isValidatingDraft, setIsValidatingDraft] =
    React.useState<boolean>(false);
  const [isPublishingDraft, setIsPublishingDraft] =
    React.useState<boolean>(false);
  const [draftPricingYearIds, setDraftPricingYearIds] = React.useState<Set<number>>(new Set());
  const [draftPricingYearsReady, setDraftPricingYearsReady] = React.useState(false);
  const [draftMinimumStartDates, setDraftMinimumStartDates] =
    React.useState<DraftMinimumStartDatesByProduct | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const pricelistRouteActive = location.pathname
    .toLowerCase()
    .startsWith("/pricelist");
  const draftState = (location.state as
    | { divisionId?: number; year?: number; centreCode?: string }
    | undefined);

  React.useEffect(() => {
    if (!props.isDraftMode) return;
    const fromNav = draftState?.centreCode?.trim();
    const stored = readSelectedCentreCode();
    const target = fromNav || stored;
    if (!target || !centres?.length) return;
    if (!centres.some((c) => c.code === target)) return;
    if (target === selectedCentreCode) return;
    props.changeSelectedCentre(target);
  }, [
    props.isDraftMode,
    draftState?.centreCode,
    centres,
    selectedCentreCode,
    props.changeSelectedCentre,
  ]);

  const DRAFT_HIGHLIGHT_STORAGE_KEY = "draft-row-highlight";
  const draftYearForStorage = draftState?.year ?? "";
  const draftHighlightStorageKey = `${DRAFT_HIGHLIGHT_STORAGE_KEY}-${divisionId}-${draftYearForStorage}`;
  const [rowHighlightColors, setRowHighlightColors] = React.useState<
    Record<string, string>
  >({});
  React.useEffect(() => {
    if (!props.isDraftMode || !divisionId) return;
    try {
      const raw = localStorage.getItem(draftHighlightStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object")
          setRowHighlightColors(parsed as Record<string, string>);
      }
    } catch {
      setRowHighlightColors({});
    }
  }, [props.isDraftMode, divisionId, draftHighlightStorageKey]);
  const handleRowHighlightChange = React.useCallback(
    (key: string, color: string | null) => {
      setRowHighlightColors((prev) => {
        const next = { ...prev };
        if (color) next[key] = color;
        else delete next[key];
        try {
          localStorage.setItem(draftHighlightStorageKey, JSON.stringify(next));
        } catch {
        }
        return next;
      });
      if (props.draftPricelist) {
        const idx = props.draftPricelist.findIndex(
          (item) => offeringKey(item as any, "") === key
        );
        if (idx >= 0) {
          const item = { ...(props.draftPricelist[idx] as any) };
          item.colorHEX = color ?? null;
          updateDraftPricelistItem(item, idx);
          setFormState(SaveState.PendingChanges);
        }
      }
    },
    [
      draftHighlightStorageKey,
      props.draftPricelist,
      updateDraftPricelistItem,
      setFormState,
    ]
  );

  const resolveDivisionId = React.useCallback(
    () => getDivisionId() || divisionId,
    [divisionId],
  );

  React.useEffect(() => {
    userPickedPricingYearRef.current = false;
    syncedDraftNavYearRef.current = undefined;
    setDraftPricingYearsReady(false);
  }, [divisionId, props.isDraftMode]);

  React.useEffect(() => {
    if (props.isDraftMode) return;
    const activeDivisionId = resolveDivisionId();
    if (!activeDivisionId) {
      setDivisionPricingYears([]);
      setDraftPricingYearIds(new Set());
      setDraftPricingYearsReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await getPricingYearsByDivision(activeDivisionId);
        if (cancelled) return;
        const asPricingYearId: PricingYearId[] = list.map((py) =>
          toPricingYearId(py, activeDivisionId)
        );
        setDivisionPricingYears(asPricingYearId);
        setDraftPricingYearIds(
          new Set(list.filter(isDraftPricingYear).map((py) => py.id)),
        );
        setDraftPricingYearsReady(true);
      } catch {
        if (!cancelled) {
          setDivisionPricingYears([]);
          setDraftPricingYearIds(new Set());
          setDraftPricingYearsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.isDraftMode, divisionId, resolveDivisionId]);

  // Draft mode
  React.useEffect(() => {
    const activeDivisionId = resolveDivisionId();
    if (!props.isDraftMode || !activeDivisionId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getPricingYearsByDivision(activeDivisionId);
        if (cancelled) return;
        const hasDraft = list.some(isDraftPricingYear);
        if (!hasDraft) {
          navigate("/new-pricing-year", { replace: true });
          return;
        }
        const asPricingYearId: PricingYearId[] = list.map((py) =>
          toPricingYearId(py, activeDivisionId)
        );
        setDivisionPricingYears(asPricingYearId);
        setDraftPricingYearIds(
          new Set(list.filter(isDraftPricingYear).map((py) => py.id)),
        );
        setDraftPricingYearsReady(true);

        const navYear =
          draftState?.year != null ? Number(draftState.year) : null;
        const navYearChanged = navYear !== syncedDraftNavYearRef.current;
        const shouldSyncYear =
          !userPickedPricingYearRef.current || navYearChanged;

        if (!shouldSyncYear) return;

        syncedDraftNavYearRef.current = navYear;

        const draft =
          list.find(
            (py: DivisionPricingYear) =>
              isDraftPricingYear(py) &&
              (navYear == null || py.year === navYear)
          ) ?? list.find(isDraftPricingYear);
        if (draft) {
          setSelectedYear({
            id: draft.id,
            year: draft.year,
            divisionId: activeDivisionId,
            isLocked: draft.isLocked ?? false,
          });
          changeSelectedPricingYear(draft.id);
        } else if (asPricingYearId.length > 0 && navYear != null) {
          const match = asPricingYearId.find((py) => py.year === navYear);
          if (match) {
            setSelectedYear(match);
            changeSelectedPricingYear(match.id);
          }
        }
      } catch {
        if (!cancelled) {
          setDivisionPricingYears([]);
          setDraftPricingYearIds(new Set());
          setDraftPricingYearsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    props.isDraftMode,
    divisionId,
    resolveDivisionId,
    draftState?.year,
    changeSelectedPricingYear,
    navigate,
  ]);

  const hasUnsavedChanges = React.useMemo(
    () =>
      formState === SaveState.PendingChanges ||
      formState === SaveState.ValidationError ||
      formState === SaveState.SaveError ||
      formState === SaveState.Saving,
    [formState],
  );

  useUnsavedChangesPrompt(
    hasUnsavedChanges,
    React.useCallback(({ proceed, reset }) => {
      setUnsavedDialog(
        (current) => current ?? { type: "navigation", proceed, reset },
      );
    }, []),
  );

  const centreSchools = React.useMemo(() => {
    if (!selectedCentreCode) return [];
    return (schools ?? [])
      .filter(
        (s) => String((s as any).centreCode) === String(selectedCentreCode),
      )
      .slice()
      .sort(
        (a, b) =>
          (toSchoolIdNumber((a as any).id) ?? 0) -
          (toSchoolIdNumber((b as any).id) ?? 0),
      );
  }, [schools, selectedCentreCode]);

  React.useEffect(() => {
    if (!pricelistRouteActive) return;
    if (props.isDraftMode && draftState?.centreCode?.trim()) return;
    const storedCentre = readSelectedCentreCode();
    if (!storedCentre || selectedCentreCode) return;
    if (!centres.some((c) => c.code === storedCentre)) return;
    changeSelectedCentre(storedCentre);
  }, [
    pricelistRouteActive,
    props.isDraftMode,
    draftState?.centreCode,
    selectedCentreCode,
    centres,
    changeSelectedCentre,
  ]);

  const centreSchoolOptions = React.useMemo(
    () =>
      centreSchools
        .map((s) => {
          const id = toSchoolIdNumber((s as any).id);
          return id ? { id, name: s.name } : null;
        })
        .filter(Boolean) as { id: number; name: string }[],
    [centreSchools],
  );

  const defaultSchoolId = React.useMemo(() => {
    if (!centreSchools.length) return undefined;
    return toSchoolIdNumber((centreSchools[0] as any).id);
  }, [centreSchools]);

  React.useEffect(() => {
    setSelectedSchoolId(undefined);
    lastAutoReloadKeyRef.current = null;

    loadPricelist([]);
    loadDraftPricelist([]);
    setFormErrors([]);
    setFormState(SaveState.Standard);
  }, [
    selectedCentreCode,
    loadPricelist,
    loadDraftPricelist,
    setFormErrors,
    setFormState,
  ]);

  const centreCurrencyIso = React.useMemo(
    () => normIso(getCentreCurrencyIso(centres, selectedCentreCode)),
    [centres, selectedCentreCode],
  );

  const { allCurrencies, currenciesLoadError } = useCurrencies();
  const enabledCurrencies = React.useMemo(
    () => getEnabledCurrencies(draftPricelist ?? [], centreCurrencyIso),
    [draftPricelist, centreCurrencyIso],
  );

  React.useEffect(() => {
    if (!selectedCentreCode || !defaultSchoolId) {
      setSelectedSchoolId(undefined);
      return;
    }

    setSelectedSchoolId((prev) => {
      const prevNum = typeof prev === "number" ? prev : undefined;
      if (
        prevNum &&
        centreSchools.some((s) => toSchoolIdNumber((s as any).id) === prevNum)
      )
        return prevNum;
      return Number(defaultSchoolId);
    });
  }, [selectedCentreCode, defaultSchoolId, centreSchools]);

  const discardChanges = React.useCallback(() => {
    setEditMode(false);
    setFormState(SaveState.Standard);
    loadDraftPricelist(pricelist ? JSON.parse(JSON.stringify(pricelist)) : []);
    setFormErrors([]);
    setPendingSchoolOps([]);
  }, [loadDraftPricelist, pricelist, setFormErrors, setFormState, setPendingSchoolOps]);

  const {
    unsavedDialog,
    setUnsavedDialog,
    requestCancelEdit: handleCancelEdit,
    confirmLeave,
    cancelLeave,
  } = useUnsavedChangesDialog({ hasUnsavedChanges, discardChanges });


  const initialYear: PricingYearId | undefined = React.useMemo(() => {
    if (!divisionPricingYears || divisionPricingYears.length === 0)
      return undefined;
    return props.isDraftMode
      ? pickDefaultPricingYear(divisionPricingYears)
      : pickPublishedDefaultPricingYear(divisionPricingYears, draftPricingYearIds);
  }, [divisionPricingYears, draftPricingYearIds, props.isDraftMode]);

  const [selectedYear, setSelectedYear] = React.useState<
    PricingYearId | undefined
  >(initialYear);

  React.useEffect(() => {
    if (!selectedYear?.id) return;
    const reduxYearValidForDivision =
      props.selectedPricingYear != null &&
      divisionPricingYears.some((py) => py.id === props.selectedPricingYear);
    if (reduxYearValidForDivision && props.selectedPricingYear === selectedYear.id) {
      return;
    }
    changeSelectedPricingYear(selectedYear.id);
  }, [
    selectedYear?.id,
    changeSelectedPricingYear,
    props.selectedPricingYear,
    divisionPricingYears,
  ]);

  React.useEffect(() => {
    if (props.isDraftMode) return;
    if (!divisionPricingYears?.length || !draftPricingYearsReady) return;

    const preferred = pickPublishedDefaultPricingYear(
      divisionPricingYears,
      draftPricingYearIds
    );
    if (!preferred) return;

    const reduxMatch =
      props.selectedPricingYear != null
        ? divisionPricingYears.find((py) => py.id === props.selectedPricingYear)
        : undefined;

    const reduxPointsAtDraft =
      reduxMatch != null && draftPricingYearIds.has(reduxMatch.id);
    const localPointsAtDraft =
      selectedYear != null && draftPricingYearIds.has(selectedYear.id);

    if (reduxPointsAtDraft || localPointsAtDraft || selectedYear == null) {
      if (selectedYear?.id !== preferred.id) setSelectedYear(preferred);
      if (props.selectedPricingYear !== preferred.id) {
        changeSelectedPricingYear(preferred.id);
      }
      return;
    }

    if (!reduxMatch) return;

    // Redux can still point at a locked year while local state was corrected.
    if (reduxMatch.isLocked) {
      if (preferred.id !== props.selectedPricingYear) {
        changeSelectedPricingYear(preferred.id);
      }
      if (preferred.id !== selectedYear?.id) setSelectedYear(preferred);
      return;
    }

    if (!userPickedPricingYearRef.current && reduxMatch.id !== selectedYear?.id) {
      setSelectedYear(reduxMatch);
    }
  }, [
    props.isDraftMode,
    divisionPricingYears,
    draftPricingYearIds,
    draftPricingYearsReady,
    props.selectedPricingYear,
    selectedYear?.id,
    changeSelectedPricingYear,
  ]);

  React.useEffect(() => {
    if (props.isDraftMode || !draftPricingYearsReady) return;
    if (draftState?.year == null) return;

    const navYear = Number(draftState.year);
    if (!Number.isFinite(navYear)) return;

    const match = divisionPricingYears.find((py) => py.year === navYear);
    if (!match || match.isLocked || draftPricingYearIds.has(match.id)) return;
    if (
      selectedYear?.id === match.id &&
      props.selectedPricingYear === match.id
    ) {
      return;
    }

    userPickedPricingYearRef.current = true;
    lastAutoReloadKeyRef.current = null;
    setSelectedYear(match);
    changeSelectedPricingYear(match.id);
  }, [
    props.isDraftMode,
    draftPricingYearsReady,
    draftState?.year,
    divisionPricingYears,
    draftPricingYearIds,
    selectedYear?.id,
    props.selectedPricingYear,
    changeSelectedPricingYear,
  ]);

  const centreName =
    props.selectedCentreCode && props.centres
      ? (props.centres.find((cnt) => cnt.code === props.selectedCentreCode)
          ?.name ?? "")
      : "";

  const isCentreInactive =
    props.selectedCentreCode && props.centres
      ? (props.centres.find((cnt) => cnt.code === props.selectedCentreCode)
          ?.active ?? true) === false
      : false;

  const validationHint = React.useMemo(
    () => getPricelistValidationSaveMessage(props.formErrors),
    [props.formErrors]
  );

  const yearOptions = React.useMemo(
    () => [...(divisionPricingYears ?? [])].sort((a, b) => b.year - a.year),
    [divisionPricingYears],
  );

  const draftPricingYearIdsKey = React.useMemo(
    () =>
      [...draftPricingYearIds]
        .sort((a, b) => a - b)
        .join(","),
    [draftPricingYearIds],
  );

  const reload = React.useCallback(
    (overridePricingYearId?: number) => {
      const pricingYearId =
        typeof overridePricingYearId === "number"
          ? overridePricingYearId
          : (selectedPricingYear as number | undefined);

      const schoolIds = centreSchools
        .map((s) => toSchoolIdNumber((s as any).id))
        .filter((id): id is number => Number.isFinite(id))
        .map((id) => ({ id }));

      if (!selectedProductType || !pricingYearId || schoolIds.length === 0)
        return;
      if (hasUnsavedChanges) return;

      const requestId = ++reloadRequestIdRef.current;
      const isCurrentRequest = () => requestId === reloadRequestIdRef.current;

      setIsLoading(true);
      const divId = getDivisionId();
      const draftYear =
        divisionPricingYears.find((py) => py.id === pricingYearId)?.year ??
        selectedYear?.year ??
        (draftState?.year != null ? Number(draftState.year) : undefined);
      const shouldUseDraftLoad =
        props.isDraftMode || draftPricingYearIds.has(pricingYearId);

      const applyLoadedOfferings = (res: unknown, loadedAsDraft: boolean) => {
        if (!isCurrentRequest()) return;

        const raw = Array.isArray(res) ? res : (res as { ok?: boolean; data?: unknown })?.ok
          ? (res as { data: unknown }).data
          : null;

        if (!Array.isArray(raw)) {
          const errRes = res as { errorMessage?: string; message?: string } | null;
          setFormState(
            SaveState.LoadError,
            errRes?.errorMessage ?? errRes?.message ?? "Failed to load pricelist.",
          );
          return;
        }

        const sanitized = raw.map((x: any) => ({
          ...x,
          dateStrategyAssignments: Array.isArray(x.dateStrategyAssignments)
            ? x.dateStrategyAssignments.filter((ds: any) => ds && ds.fromDate)
            : [],
          pricePeriods: Array.isArray(x.pricePeriods) ? x.pricePeriods : [],
        }));

        const merged = (() => {
          if (selectedProductType !== "Fee") return sanitized;
          const existingDraftFees = (draftPricelistRef.current ?? []).filter(
            isFeeOffering,
          ) as any[];
          const pendingFees = existingDraftFees.filter(
            (f) => f?.hasPendingChanges === true || f?.isNew === true
          );
          if (!pendingFees.length) return sanitized;

          const serverFeeIds = new Set<number>(
            sanitized
              .filter(isFeeOffering)
              .map((x: any) => Number(x?.feeId?.id ?? 0))
              .filter((n: number) => Number.isFinite(n) && n > 0)
          );

          const toAdd = pendingFees.filter((x) => {
            const id = Number(x?.feeId?.id ?? 0);
            if (!Number.isFinite(id) || id <= 0) return false;
            return !serverFeeIds.has(id);
          });

          return toAdd.length ? [...sanitized, ...toAdd] : sanitized;
        })();

        loadPricelist(merged);
        loadDraftPricelist(JSON.parse(JSON.stringify(merged)));
        setFormState(SaveState.Standard);
        setFormErrors([]);

        if (loadedAsDraft && sanitized.length > 0) {
          const fromServer: Record<string, string> = {};
          sanitized.forEach((item: any) => {
            const key = offeringKey(item, "");
            const hex = item?.colorHEX;
            if (key && hex && typeof hex === "string" && hex.trim()) {
              fromServer[key] = hex.trim();
            }
          });
          setRowHighlightColors((prev) => ({ ...prev, ...fromServer }));
        }
      };

      const loadDraft = () =>
        loadDraftOfferings(divId, {
          year: draftYear!,
          schoolIds,
          productType: selectedProductType,
        });

      const loadPublished = () =>
        (api as any).GetAllActiveOfferings(
          {
            schoolIds,
            pricingYearId,
            type: selectedProductType as OfferingType,
          },
          divId,
        );

      const fetchPromise =
        shouldUseDraftLoad && draftYear != null ? loadDraft() : loadPublished();

      const finishLoading = () => {
        if (isCurrentRequest()) setIsLoading(false);
      };

      Promise.resolve(fetchPromise)
        .then((res: unknown) => {
          if (!isCurrentRequest()) return;
          applyLoadedOfferings(res, shouldUseDraftLoad);
        })
        .catch((err) => {
          if (!isCurrentRequest()) return;

          const message = String((err as Error)?.message ?? err);
          const ticksError = /ticks/i.test(message);
          if (ticksError && draftYear != null && !shouldUseDraftLoad) {
            return loadDraft()
              .then((draftRes) => {
                if (!isCurrentRequest()) return;
                applyLoadedOfferings(draftRes, true);
              })
              .catch((draftErr) => {
                if (!isCurrentRequest()) return;
                setFormState(
                  SaveState.LoadError,
                  String((draftErr as Error)?.message ?? draftErr),
                );
              })
              .finally(finishLoading);
          }
          setFormState(SaveState.LoadError, message);
        })
        .finally(finishLoading);
    },
    [
      selectedPricingYear,
      selectedYear?.id,
      selectedYear?.year,
      selectedProductType,
      hasUnsavedChanges,
      centreSchools,
      divisionPricingYears,
      draftPricingYearIdsKey,
      props.isDraftMode,
      draftState?.year,
      setIsLoading,
      loadPricelist,
      loadDraftPricelist,
      setFormState,
      setFormErrors,
    ],
  );

  const reloadRef = React.useRef(reload);
  React.useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  const attemptAutoReload = React.useCallback(() => {
    if (!pricelistRouteActive) return;
    if (!resolveDivisionId()) return;
    if (!selectedCentreCode) return;
    if (!selectedProductType) return;
    if (!centreSchools.length) return;
    if (!draftPricingYearsReady) return;
    if (hasUnsavedChanges) return;

    let yearId =
      selectedYear?.id ??
      (props.selectedPricingYear != null &&
      divisionPricingYears.some((py) => py.id === props.selectedPricingYear)
        ? props.selectedPricingYear
        : undefined);

    if (!props.isDraftMode) {
      if (yearId != null && draftPricingYearIds.has(yearId)) {
        yearId = pickPublishedDefaultPricingYear(
          divisionPricingYears,
          draftPricingYearIds
        )?.id;
      }
    } else if (yearId == null) {
      yearId = pickDefaultPricingYear(divisionPricingYears)?.id;
    }

    if (!yearId) return;

    const schoolIdsKey = centreSchools
      .map((s) => toSchoolIdNumber((s as any).id))
      .filter((id): id is number => Number.isFinite(id))
      .join(",");
    const reloadKey = `${yearId}|${selectedCentreCode}|${selectedProductType}|${schoolIdsKey}`;
    if (lastAutoReloadKeyRef.current === reloadKey) return;
    lastAutoReloadKeyRef.current = reloadKey;

    reloadRef.current(yearId);
  }, [
    pricelistRouteActive,
    props.isDraftMode,
    resolveDivisionId,
    selectedCentreCode,
    selectedProductType,
    centreSchools,
    draftPricingYearsReady,
    hasUnsavedChanges,
    selectedPricingYear,
    selectedYear?.id,
    divisionPricingYears,
    draftPricingYearIdsKey,
  ]);

  React.useEffect(() => {
    const syncDivisionId = () => {
      const current = getDivisionId();
      setDivisionId((prev) => (prev !== current ? current : prev));
    };
    const onDivisionChanged = () => {
      syncDivisionId();
      window.setTimeout(() => attemptAutoReload(), 0);
    };
    const onPageReady = () => {
      syncDivisionId();
      window.setTimeout(() => attemptAutoReload(), 0);
    };

    syncDivisionId();
    window.addEventListener("selectedDivisionChanged", onDivisionChanged);
    window.addEventListener(PRICELIST_PAGE_READY, onPageReady);
    return () => {
      window.removeEventListener("selectedDivisionChanged", onDivisionChanged);
      window.removeEventListener(PRICELIST_PAGE_READY, onPageReady);
    };
  }, [attemptAutoReload]);

  React.useEffect(() => {
    attemptAutoReload();
  }, [attemptAutoReload]);

  const activePricingYear = React.useMemo(
    () =>
      resolveActivePricingYear({
        pricingYears: divisionPricingYears,
        selectedPricingYearId: selectedPricingYear,
        selectedYear,
      }),
    [divisionPricingYears, selectedPricingYear, selectedYear],
  );

  React.useEffect(() => {
    if (!draftPricingYearsReady || !divisionPricingYears.length) return;
    if (
      selectedPricingYear != null &&
      divisionPricingYears.some((py) => py.id === selectedPricingYear)
    ) {
      return;
    }

    const resolved =
      activePricingYear ??
      pickPublishedDefaultPricingYear(divisionPricingYears, draftPricingYearIds);
    if (!resolved) return;

    if (selectedPricingYear !== resolved.id) {
      changeSelectedPricingYear(resolved.id);
    }
    if (selectedYear?.id !== resolved.id) {
      setSelectedYear(resolved);
    }
  }, [
    draftPricingYearsReady,
    divisionPricingYears,
    draftPricingYearIds,
    selectedPricingYear,
    selectedYear?.id,
    activePricingYear,
    changeSelectedPricingYear,
  ]);

  const selectedPricingYearObj = activePricingYear;

  React.useEffect(() => {
    if (!props.isDraftMode || !divisionId) {
      setDraftMinimumStartDates(null);
      return;
    }

    const year =
      selectedYear?.year ??
      selectedPricingYearObj?.year ??
      draftState?.year;

    if (!year) return;

    let cancelled = false;

    (async () => {
      try {
        const dates = await getDraftMinimumStartDates(divisionId, Number(year));
        if (!cancelled) setDraftMinimumStartDates(dates);
      } catch {
        if (!cancelled) setDraftMinimumStartDates(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    props.isDraftMode,
    divisionId,
    selectedYear?.year,
    selectedPricingYearObj?.year,
    draftState?.year,
  ]);

  const pricingYearWindow = React.useMemo(
      () => getPricingYearWindow(selectedPricingYearObj),
      [selectedPricingYearObj]
  );

  const productAllowedStartDate = React.useMemo(
      () =>
          props.isDraftMode
              ? resolveProductAllowedStartDate(
                  props.selectedProductType,
                  pricingYearWindow,
                  draftMinimumStartDates
                )
              : pricingYearWindow.allowedStartDate,
      [
        props.isDraftMode,
        props.selectedProductType,
        pricingYearWindow,
        draftMinimumStartDates,
      ]
  );

  const productAllowedEndDate = pricingYearWindow.allowedEndDate;

  const latestSelectedValues = React.useRef({
    selectedPricingYear,
    selectedSchoolId,
  });

  React.useEffect(() => {
    latestSelectedValues.current = { selectedPricingYear, selectedSchoolId };
  }, [selectedPricingYear, selectedSchoolId]);

  const refreshYears = React.useCallback(async () => {
    if (!divisionId) return;
    try {
      const list = await getPricingYearsByDivision(divisionId);
      const asPricingYearId: PricingYearId[] = (list ?? []).map((py) =>
        toPricingYearId(py, divisionId)
      );
      setDivisionPricingYears(asPricingYearId);
      setDraftPricingYearIds(
        new Set((list ?? []).filter(isDraftPricingYear).map((py) => py.id)),
      );
    } catch {
      // If refetch fails, keep existing dropdown options.
    }
  }, [divisionId]);

  const refreshGridEvent = async (evt?: { eventType?: unknown }) => {
    //const eventType = String((evt as any)?.eventType ?? "").trim();
    //debugger;

    //if (eventType === PublishedEventType.PricingYearEventPublished) {
      
    //}
    await refreshYears();

    const { selectedPricingYear, selectedSchoolId } =
      latestSelectedValues.current;
    lastAutoReloadKeyRef.current = null;
    reloadRef.current(selectedPricingYear);
  };

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent(
    [
      PublishedEventType.ProductOfferingEventPublished,
      PublishedEventType.PricePeriodEventPublished,
      PublishedEventType.DateStrategyEventPublished,
      PublishedEventType.DecommissionDateEventPublished,
      PublishedEventType.PricingYearEventPublished
    ],
    refreshGridEvent,
    divisionId,
    selectedSchoolId,
    selectedProductType as OfferingType,
  );

  const formatDateStrategy = (dateStrategy: DateStrategyAssign): string[] => {
    if (DateStrategy[dateStrategy.strategyTypeId] === "Recurrence") {
      const label = formatRecurranceStartDaysLabel(dateStrategy.recurranceStartDays);
      return label ? label.split(", ") : [];
    } else if (
      DateStrategy[dateStrategy.strategyTypeId] === "Fixed Duration" ||
      DateStrategy[dateStrategy.strategyTypeId] === "Specific Start Dates"
    ) {
      const selectedYearObj = selectedPricingYearObj;
      if (!selectedYearObj) return [];
      if (
        dateStrategy.specificStartDates &&
        dateStrategy.specificStartDates.length > 0
      ) {
        return dateStrategy.specificStartDates
          .filter(
            (startDate) =>
              new Date(startDate.date).getFullYear() === selectedYearObj.year,
          )
          .map((date) => `${DateHelper.ToDateString(date.date)}`);
      } else if (
        dateStrategy.fixedDurationDates &&
        dateStrategy.fixedDurationDates.length > 0
      ) {
        return dateStrategy.fixedDurationDates
          .filter(
            (dates) =>
              new Date(dates.startDate).getFullYear() === selectedYearObj.year,
          )
          .map(
            (dates) =>
              `${DateHelper.ToDateString(
                dates.startDate,
              )} - ${DateHelper.ToDateString(dates.endDate)}`,
          );
      }
    } else if (DateStrategy[dateStrategy.strategyTypeId] === "Offering Dates") {
      return [`From ${DateHelper.ToDateString(dateStrategy.fromDate)}`];
    }
    return [];
  };

  const currencyMenuItems = [
    {
      key: "add-currency",
      label: "Manage currency",
      onClick: () => {
        if (!editMode) {
          setEditMode(true);
          props.setFormState(SaveState.NoChanges);
        }
        setAddCurrencyModalVisible(true);
      },
    },
  ];

  const queueSchoolOp = React.useCallback(
    (params: {
      op: "create" | "recommission" | "decommission" | null;
      offering: EditPricelistResponse;
      schoolId: number;
    }) => {
      const { op, offering, schoolId } = params;
      const productIdText = String((offering as any).productIdText ?? "");
      const type = (offering as any).type as OfferingType;
      const pricingYearValue =
        resolveActivePricingYear({
          pricingYears: divisionPricingYears,
          selectedPricingYearId: props.selectedPricingYear,
          selectedYear,
        })?.year ?? new Date().getFullYear();

      if (!productIdText || !type || !Number.isFinite(schoolId)) return;

      setPendingSchoolOps((prev) => {
        const key = `${productIdText}|${schoolId}`;
        const filtered = prev.filter(
          (item) => `${item.productIdText}|${item.schoolId}` !== key
        );
        if (!op) return filtered;
        return [
          ...filtered,
          { op, productIdText, type, pricingYear: pricingYearValue, schoolId },
        ];
      });
    },
    [divisionPricingYears, props.selectedPricingYear, selectedYear]
  );

  const productOfferingsToCSV = () => {
    let csvHeader = "";
    let csvContent = "";
    const centre = props.centres.find(
      (c) => c.code === props.selectedCentreCode,
    );
    if (!centre || !props.pricelist || !props.selectedProductType)
      return `${csvHeader}\n${csvContent}`;

    csvHeader =
      "Id,Product Name,Date Range From,Date Range To,Weeks Bracket From,Weeks Bracket To,Centre,Currency,Price Major,Price Minor,Date Strategy,Dates";

    const isValidRow = (
      p: EditPricelistResponse,
    ): p is StandardOfferingDto | PackageOfferingDto => {
      const selectedType = props.selectedProductType as OfferingType;
      return (
        isNonFeeOffering(p) &&
        p.type === selectedType &&
        Array.isArray(p.pricePeriods) &&
        p.pricePeriods.length > 0 &&
        Array.isArray(p.dateStrategyAssignments) &&
        p.dateStrategyAssignments.length > 0
      );
    };

    [...props.pricelist]
      .filter(isValidRow)
      .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""))
      .forEach((p) => {
        const dateStrategy = [...(p.dateStrategyAssignments ?? [])].sort(
          (a, b) =>
            (DateHelper.DateManipulation(
              b.fromDate,
              a.fromDate,
              ManipulationTypes.Subtract,
            ) as any) ?? 0,
        )[0];
        if (!dateStrategy) return;

        (p.pricePeriods ?? []).forEach((pp: PricePeriods) => {
          (pp.priceBands ?? []).forEach((pb: PriceBands) => {
            const dates = formatDateStrategy(dateStrategy);
            const prices = Array.isArray(pb.prices) ? pb.prices : [];
            const priceRows = prices.length
              ? prices
              : [
                  {
                    currencyIsoCode: "",
                    pricePerMajorUnit: "",
                    pricePerMinorUnit: "",
                  },
                ];

            dates.forEach((value: string) => {
              priceRows.forEach((price: any) => {
                csvContent +=
                  `${p.productId?.id ?? ""},` +
                  `"${p.name ?? ""}",` +
                  `${DateHelper.ToDateString(pp.startDate)},` +
                  `${DateHelper.ToDateString(pp.endDate)},` +
                  `${pb.minUnits},` +
                  `${isAnyUnits(pb.maxUnits) ? "Any" : pb.maxUnits},` +
                  `${centre.name},` +
                  `${price.currencyIsoCode ?? ""},` +
                  `${price.pricePerMajorUnit ?? ""},` +
                  `${price.pricePerMinorUnit ?? ""},` +
                  `${DateStrategy[dateStrategy.strategyTypeId] ?? ""},` +
                  `${value} \n`;
              });
            });
          });
        });
      });

    return `${csvHeader} \n ${csvContent}`;
  };

  const getSchoolOpsSuccessMessage = (
      hasActiveOps: boolean,
      hasInactiveOps: boolean
  ) => {
    if (hasActiveOps && hasInactiveOps) return "Schools updated.";
    if (hasActiveOps) return "School activated.";
    if (hasInactiveOps) return "School deactivated.";
    return "School updated.";
  };

  const getSchoolIdValue = React.useCallback((input: any): number | null => {
    if (input == null) return null;
    if (typeof input === "object") {
      const c: any = input;
      if ("id" in c) return getSchoolIdValue(c.id);
      if ("Id" in c) return getSchoolIdValue(c.Id);
      if ("schoolId" in c) return getSchoolIdValue(c.schoolId);
      if ("SchoolId" in c) return getSchoolIdValue(c.SchoolId);
    }
    const n = Number(input);
    return Number.isFinite(n) ? n : null;
  }, []);

  const getFeeBaselineStatus = React.useCallback((offering: any, schoolId: number): SchoolOfferingStatus => {
    const status = (offering?.schoolStatuses ?? []).find((s: any) => {
      const sid = getSchoolIdValue((s as any)?.schoolId);
      return sid === schoolId;
    })?.status;
    return (status ?? "Disabled") as SchoolOfferingStatus;
  }, [getSchoolIdValue]);

  const handleSave = async (): Promise<{ ok: boolean; message?: string }> => {
    if (!props.selectedPricingYear) {
      return {
        ok: false,
        message: "Please select a pricing year before saving.",
      };
    }

    if (!props.draftPricelist) {
      return { ok: false, message: "No pricelist is loaded to save." };
    }

    const pricingYearRec = resolveActivePricingYear({
      pricingYears: divisionPricingYears,
      selectedPricingYearId: props.selectedPricingYear,
      selectedYear,
    });
    if (!pricingYearRec) {
      props.setFormState(SaveState.SaveError, "Pricing year not found.");
      return { ok: false, message: "Pricing year not found." };
    }

    const resolvedDivisionId = getDivisionId() || divisionId;
    const offeringType = props.selectedProductType as OfferingType;
    const toApiOffering = (item: EditPricelistResponse) =>
        prepareOfferingForApi(item, pricingYearRec.year, enabledCurrencies);


    const pushApiErrors = (prefix: string, r: any, errors: string[]) => {
      if (!r?.ok) {
        if (Array.isArray(r?.errors) && r.errors.length > 0) {
          r.errors.forEach((e: string) => {
            const mapped = mapPricelistApiErrorMessage(e);
            errors.push(mapped === e ? `${prefix}: ${e}` : mapped);
          });
        } else {
          const mapped = mapPricelistApiErrorMessage(r?.message || "Unknown error");
          errors.push(
            mapped === r?.message ? `${prefix}: ${mapped}` : mapped
          );
        }
      }
    };

    setIsSaving(true);
    props.setFormState(SaveState.Saving);

    const errors: string[] = [];

    let hadSchoolOps = false;
    let hasActiveSchoolOps = false;
    let hasInactiveSchoolOps = false;

    try {
      // Draft pricing year
      const isDraftYear = draftPricingYearIds.has(pricingYearRec.id);
      if (isDraftYear) {
        if (offeringType === "Fee" && props.formErrors && props.formErrors.length > 0) {
          const validationMessage = getPricelistValidationSaveMessage(props.formErrors);
          props.setFormState(SaveState.ValidationError);
          return {
            ok: false,
            message: validationMessage,
          };
        }
        const pricelistPayload = (props.draftPricelist ?? []).map((item) =>
          toApiOffering(item),
        );
        await saveDraftPricelistContent(
          resolvedDivisionId,
          pricingYearRec.year,
          pricelistPayload,
          offeringType,
        );
        setEditMode(false);
        props.setFormState(SaveState.Saved);
        reload(pricingYearRec.id);
        return { ok: true, message: "Changes saved successfully." };
      }

      if (offeringType === "Fee") {
        if (props.formErrors && props.formErrors.length > 0) {
          const validationMessage = getPricelistValidationSaveMessage(props.formErrors);
          props.setFormState(SaveState.ValidationError);
          return {
            ok: false,
            message: validationMessage,
          };
        }

        const baselineByKey = new Map<string, FeeOfferingDto>(
          (props.pricelist ?? [])
            .filter(isFeeOffering)
            .map((x: any) => [String(x.productIdText), x as FeeOfferingDto])
        );

        const draftFees = (props.draftPricelist ?? []).filter(isFeeOffering) as FeeOfferingDto[];
        const createOfferings: FeeOfferingDto[] = [];
        const updateOfferings: FeeOfferingDto[] = [];
        const activateRequests: { productIdText: string; schoolIds: { id: number }[] }[] = [];
        const deactivateRequests: { productIdText: string; schoolIds: { id: number }[] }[] = [];

        draftFees.forEach((draft) => {
          const baseline = baselineByKey.get(String(draft.productIdText));
          const selectedSchoolIds = new Set(
              (draft.schoolIds ?? [])
                  .map((s: any) => getSchoolIdValue(s))
                  .filter((id: any): id is number => id != null)
          );

          if (draft.isNew || !baseline) {
            if (selectedSchoolIds.size === 0) {
              errors.push(`${draft.name}: select at least one school before creating.`);
              return;
            }

            const prepared = toApiOffering(draft) as FeeOfferingDto;
            const knownStatuses = (draft.schoolStatuses ?? []) as any[];
            const idsForCreate = knownStatuses.length === 0
              ? [...selectedSchoolIds]
              : [...selectedSchoolIds].filter((id) => {
                  const entry = knownStatuses.find((ss: any) =>
                    getSchoolIdValue((ss as any)?.schoolId) === id
                  );
                  return !entry || (entry as any).status === "Disabled";
                });

            if (idsForCreate.length === 0) return;

            createOfferings.push({
              ...prepared,
              schoolIds: idsForCreate.map((id) => ({ id })),
            });
            return;
          }

          const availableSchoolIds = new Set<number>();
          (draft.schoolStatuses ?? []).forEach((s: any) => {
            const sid = getSchoolIdValue((s as any)?.schoolId);
            if (sid != null) availableSchoolIds.add(sid);
          });
          selectedSchoolIds.forEach((sid) => availableSchoolIds.add(sid));

          const createIds: number[] = [];
          const activateIds: number[] = [];
          const deactivateIds: number[] = [];
          const activeIds: number[] = [];

          availableSchoolIds.forEach((schoolId) => {
            const baselineStatus = getFeeBaselineStatus(baseline, schoolId);
            const checked = selectedSchoolIds.has(schoolId);

            if (checked && baselineStatus === "Disabled") createIds.push(schoolId);
            if (checked && baselineStatus === "Inactive") activateIds.push(schoolId);
            if (!checked && baselineStatus === "Active") deactivateIds.push(schoolId);
            if (checked && baselineStatus === "Active") {
              activeIds.push(schoolId);
            }
          });

          if (createIds.length > 0) {
            const prepared = toApiOffering(draft) as FeeOfferingDto;
            createOfferings.push({
              ...prepared,
              schoolIds: createIds.map((id) => ({ id })),
            });
          }

          if (activateIds.length > 0) {
            activateRequests.push({
              productIdText: String(draft.productIdText),
              schoolIds: activateIds.map((id) => ({ id })),
            });
          }

          if (deactivateIds.length > 0) {
            deactivateRequests.push({
              productIdText: String(draft.productIdText),
              schoolIds: deactivateIds.map((id) => ({ id })),
            });
          }

          const hasContentChanges = !isSameOfferingForUpdate(
              baseline,
              { ...draft, schoolIds: baseline.schoolIds } as any,
              centreCurrencyIso || ""
          );

          if (hasContentChanges && activeIds.length > 0) {
            const prepared = toApiOffering(draft) as FeeOfferingDto;

            updateOfferings.push({
              ...prepared,
              schoolIds: activeIds.map((id) => ({ id })),
            });
          }
        });

        if (createOfferings.length > 0) {
          const createRequest: UpsertOfferingsRequest = { type: "Fee", offerings: createOfferings };
          const result = await api.CreateOfferings(createRequest, pricingYearRec.year, resolvedDivisionId);
          pushApiErrors("Failed to create offerings", result, errors);
        }

        if (updateOfferings.length > 0) {
          const updateRequest: UpsertOfferingsRequest = { type: "Fee", offerings: updateOfferings };
          const result = await api.PutPricelistForSchools(updateRequest, pricingYearRec.year, resolvedDivisionId);
          pushApiErrors("Failed to update offerings", result, errors);
        }

        for (const req of activateRequests) {
          const result = await api.ActivateOfferings(
            { type: "Fee", pricingYear: pricingYearRec.year, productIdText: req.productIdText, schoolIds: req.schoolIds },
            resolvedDivisionId
          );
          pushApiErrors("Failed to activate offerings", result, errors);
        }

        for (const req of deactivateRequests) {
          const result = await api.DeactivateOfferings(
            { type: "Fee", pricingYear: pricingYearRec.year, productIdText: req.productIdText, schoolIds: req.schoolIds, decommissionDate: DateHelper.ToDateString(new Date(), "yyyy-MM-dd") },
            resolvedDivisionId
          );
          pushApiErrors("Failed to deactivate offerings", result, errors);
        }

        if (errors.length > 0) {
          const msg = formatPricelistApiErrors(errors);
          props.setFormState(SaveState.SaveError, msg);
          return { ok: false, message: msg };
        }

        // Lock any fee offerings that were just created so they show as "publishing"
        // until the next SignalR-driven reload reconciles the backend state.
        const affectedFeeIds = new Set<number>();
        createOfferings.forEach((o: any) => {
          const id = Number(o?.feeId?.id ?? 0);
          if (Number.isFinite(id) && id > 0) affectedFeeIds.add(id);
        });
        updateOfferings.forEach((o: any) => {
          const id = Number(o?.feeId?.id ?? 0);
          if (Number.isFinite(id) && id > 0) affectedFeeIds.add(id);
        });
        activateRequests.forEach((r) => {
          const id = Number(String(r.productIdText ?? "").replace(/\D+/g, ""));
          if (Number.isFinite(id) && id > 0) affectedFeeIds.add(id);
        });
        deactivateRequests.forEach((r) => {
          const id = Number(String(r.productIdText ?? "").replace(/\D+/g, ""));
          if (Number.isFinite(id) && id > 0) affectedFeeIds.add(id);
        });

        if (affectedFeeIds.size > 0 && Array.isArray(props.draftPricelist)) {
          const next = props.draftPricelist.map((entry: any) => {
            if (!isFeeOffering(entry)) return entry;
            const feeId = Number(entry?.feeId?.id ?? 0);
            if (!Number.isFinite(feeId) || feeId <= 0) return entry;
            if (!affectedFeeIds.has(feeId)) return entry;
            return { ...entry, hasPendingChanges: true };
          });
          props.loadDraftPricelist(next);
        }

        setEditMode(false);
        props.setFormState(SaveState.Saved);
        reload(pricingYearRec.id);
        return { ok: true, message: "Changes saved successfully." };
      }

      const hasCreateOps = pendingSchoolOps.some((op) => op.op === "create");
      if (hasCreateOps && props.formErrors && props.formErrors.length > 0) {
        const validationMessage = getPricelistValidationSaveMessage(props.formErrors);
        props.setFormState(SaveState.ValidationError);
        return {
          ok: false,
          message: validationMessage,
        };
      }

      if (pendingSchoolOps.length > 0) {
        hadSchoolOps = true;

        const grouped = new Map<
          string,
          {
            op: "create" | "recommission" | "decommission";
            type: OfferingType;
            pricingYear: number;
            productIdText: string;
            schoolIds: { id: number }[];
          }
        >();

        pendingSchoolOps.forEach((op) => {
          const key = `${op.op}|${op.type}|${op.pricingYear}|${op.productIdText}`;
          const entry =
            grouped.get(key) ??
            {
              op: op.op,
              type: op.type,
              pricingYear: op.pricingYear,
              productIdText: op.productIdText,
              schoolIds: [],
            };
          entry.schoolIds.push({ id: op.schoolId });
          grouped.set(key, entry);
        });

        for (const entry of grouped.values()) {
          if (entry.op === "decommission") hasInactiveSchoolOps = true;
          else hasActiveSchoolOps = true;
        }

        for (const entry of grouped.values()) {
          if (entry.op === "create") {
            const currentRow = (props.draftPricelist ?? []).find(
              (o) =>
                String((o as any).productIdText) === entry.productIdText &&
                String((o as any).type) === String(entry.type),
            );
            if (!currentRow) {
              return {
                ok: false,
                message: `Unable to find offering for ${entry.productIdText}.`,
              };
            }

            const offeringForApi = toApiOffering(currentRow as any);
            const createRequest: UpsertOfferingsRequest = {
              type: entry.type,
              copyExistingDateStrategies: true,
              offerings: [{ ...offeringForApi, schoolIds: entry.schoolIds }],
            };
            const result = await api.CreateOfferings(
              createRequest,
              entry.pricingYear,
              resolvedDivisionId,
            );
            pushApiErrors("Failed to create offerings", result, errors);
          } else if (entry.op === "recommission") {
            const result = await api.Recommission(
              {
                type: entry.type,
                pricingYear: entry.pricingYear,
                productIdText: entry.productIdText,
                schoolIds: entry.schoolIds,
              },
              resolvedDivisionId,
            );
            pushApiErrors("Failed to recommission offerings", result, errors);
          } else if (entry.op === "decommission") {
            const result = await api.Decommission(
              {
                type: entry.type,
                pricingYear: entry.pricingYear,
                productIdText: entry.productIdText,
                schoolIds: entry.schoolIds,
                decommissionDate: DateHelper.ToDateString(new Date(), "yyyy-MM-dd"),
              },
              resolvedDivisionId,
            );
            pushApiErrors("Failed to decommission offerings", result, errors);
          }
        }

        if (errors.length) {
          const msg = formatPricelistApiErrors(errors);
          props.setFormState(SaveState.SaveError, msg);
          return { ok: false, message: msg };
        }

        setPendingSchoolOps([]);
      }

      if (props.formErrors && props.formErrors.length > 0) {
        const validationMessage = getPricelistValidationSaveMessage(props.formErrors);
        props.setFormState(SaveState.ValidationError);
        return {
          ok: false,
          message: validationMessage,
        };
      }

      const fallbackIso = centreCurrencyIso || "";
      const origByKey = new Map<string, EditPricelistResponse>(
        (props.pricelist ?? []).map((o) => [
          offeringKey(o as any, fallbackIso),
          o,
        ]),
      );

      const touched = (props.draftPricelist ?? []).filter(
        (o) => o.isNew === true || o.isUpdated === true,
      );

      const newOfferings = touched.filter((o) => o.isNew === true);

      const existingChangedOfferings = touched
        .filter((o) => o.isNew !== true)
        .filter((d) => {
          const orig = origByKey.get(offeringKey(d as any, fallbackIso));
          if (!orig) return true;
          return !isSameOfferingForUpdate(orig, d, fallbackIso);
        });

      const offeringsToValidate = [...newOfferings, ...existingChangedOfferings];
      for (const offering of offeringsToValidate) {
        const workingWeeksError = validateOfferingWorkingWeeksDateStrategies(
          offering as any,
          hadSchoolOps
        );
        if (workingWeeksError) {
          const message = workingWeeksError;
          props.setFormState(SaveState.ValidationError, message);
          return { ok: false, message };
        }
      }

      if (newOfferings.length === 0 && existingChangedOfferings.length === 0) {
        props.setFormState(SaveState.Saved);
        setEditMode(false);
        reload(pricingYearRec.id);

        if (hadSchoolOps && (hasActiveSchoolOps || hasInactiveSchoolOps)) {
          return {
            ok: true,
            message: getSchoolOpsSuccessMessage(hasActiveSchoolOps, hasInactiveSchoolOps),
          };
        }

        return { ok: true, message: "No changes to save." };
      }

      if (newOfferings.length > 0) {
        const createRequest: UpsertOfferingsRequest = {
          type: offeringType,
          offerings: newOfferings.map((item) => toApiOffering(item)),
        };

        const result = await api.CreateOfferings(
          createRequest,
          pricingYearRec.year,
          resolvedDivisionId,
        );
        pushApiErrors("Failed to create offerings", result, errors);
      }

      if (existingChangedOfferings.length > 0) {
        const updateRequest: UpsertOfferingsRequest = {
          type: offeringType,
          offerings: existingChangedOfferings.map((item) => toApiOffering(item))
        };

        const result = await api.PutPricelistForSchools(
          updateRequest,
          pricingYearRec.year,
          resolvedDivisionId,
        );
        pushApiErrors("Failed to update offerings", result, errors);
      }

      if (errors.length > 0) {
        const msg = formatPricelistApiErrors(errors);
        props.setFormState(SaveState.SaveError, msg);
        return { ok: false, message: msg };
      }

      setEditMode(false);
      props.setFormState(SaveState.Saved);
      reload(pricingYearRec.id);

      return { ok: true, message: "Changes saved successfully." };
    } catch (err: any) {
      const msg = mapPricelistApiErrorMessage(String(err?.message ?? err));
      props.setFormState(SaveState.SaveError, msg);
      return { ok: false, message: msg };
    } finally {
      setIsSaving(false);
    }
  };

  const isViewingDraftYear =
    props.isDraftMode ||
    (typeof props.selectedPricingYear === "number" &&
      draftPricingYearIds.has(props.selectedPricingYear));

  const draftYear = selectedYear?.year ?? draftState?.year;
  const handleDraftValidate = React.useCallback(async () => {
    const divId = getDivisionId() || divisionId;
    if (!draftYear || !divId) {
      message.warning("Select a centre and ensure draft year is loaded.");
      return;
    }
    setIsValidatingDraft(true);
    setLastDraftValidation(null);
    try {
      const result = await fullyValidateDraftPricingYear(divId, Number(draftYear));
      setLastDraftValidation(result);
      if (result?.canPublish) {
        message.success("Validation passed. You can publish the draft.");
      } else {
        const msg =
          result?.summary?.messages?.join("; ") ||
          result?.errors?.join("; ") ||
          "Validation found issues. Fix them before publishing.";
        message.warning(msg);
        if (result?.rowIssues?.length) {
          props.setFormErrors(
            result.rowIssues.map((r) => ({
              field: r.field ?? "",
              message: r.message ?? "Validation issue",
              name: "",
              error: r.message ?? "Validation issue",
            }))
          );
        }
      }
    } catch (err) {
      const anyErr = err as any;
      const items: { message: string; metadata?: any }[] | null = Array.isArray(anyErr?.errorItems)
        ? anyErr.errorItems
        : null;

      if (items && items.length > 0) {
        const formatDraftValidationItem = (it: { message: string; metadata?: any }) => {
          const msg = String(it?.message ?? "").trim();
          if (!msg) return "";
          const md = it?.metadata ?? {};
          const offeringName = md?.OfferingName ?? md?.offeringName;
          const productType = md?.ProductType ?? md?.productType;
          const prefix = [productType ? `${productType} - ` : null, offeringName]
            .filter(Boolean)
            .join(" ");
          return prefix ? `${prefix}: ${msg}` : msg;
        };

        const byCentre = new Map<string, Set<string>>();
        for (const it of items) {
          const centreName =
            it?.metadata?.CentreName ??
            it?.metadata?.centreName ??
            it?.metadata?.centerName ??
            "Unknown centre";
          const formatted = formatDraftValidationItem(it);
          if (!formatted) continue;
          const set = byCentre.get(String(centreName)) ?? new Set<string>();
          set.add(formatted);
          byCentre.set(String(centreName), set);
        }

        const centres = [...byCentre.entries()].sort((a, b) => a[0].localeCompare(b[0]));

        Modal.error({
          title: "Validation failed",
          width: 720,
          content: (
            <div style={{ maxHeight: 420, overflow: "auto", paddingRight: 8 }}>
              {centres.map(([centre, msgs]) => (
                <div key={centre} style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{centre}</div>
                  <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: 20 }}>
                    {[...msgs].sort((a, b) => a.localeCompare(b)).map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ),
        });
      } else {
        message.error(err instanceof Error ? err.message : "Validation failed.");
      }
      setLastDraftValidation({ canPublish: false });
    } finally {
      setIsValidatingDraft(false);
    }
  }, [divisionId, draftYear, props]);
  const handleDraftPublish = React.useCallback(async () => {
    const divId = getDivisionId() || divisionId;
    if (!draftYear || !divId) return;
    if (!lastDraftValidation?.canPublish) {
      message.warning("Run Validate first and fix any errors before publishing.");
      return;
    }
    setIsPublishingDraft(true);
    try {
      await publishDraftPricingYear(divId, Number(draftYear));
      message.success("Draft published successfully.");
      setLastDraftValidation(null);
      navigate("/pricelist");
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Publish failed.");
    } finally {
      setIsPublishingDraft(false);
    }
  }, [divisionId, draftYear, lastDraftValidation?.canPublish, navigate]);

  if (!divisionPricingYears || divisionPricingYears.length === 0) {
    return (
      <Alert
        title="No pricing years found"
        description="There are no pricing years configured for this division yet. Create a new pricing year or switch to another division to load a pricelist."
        type="info"
      />
    );
  }

  return (
    <div className="container">
      <div className="content">
        <Row>
          <Col span={24}>
            <Space>
              <Title
                level={2}
                id="pricelist-header"
                style={{ marginBottom: 16 }}
              >
                {props.isDraftMode && isViewingDraftYear ? (
                  <>
                    New Pricing Year {draftYear ?? draftState?.year ?? ""}
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      Draft
                    </Tag>
                  </>
                ) : (
                  `${centreName} Pricelist`
                )}
              </Title>
              {!props.isDraftMode || !isViewingDraftYear ? (
                <Select
                  className="year-selector"
                  disabled={
                    props.formState === SaveState.PendingChanges ||
                    props.formState === SaveState.NoChanges ||
                    props.formState === SaveState.ValidationError ||
                    props.formState === SaveState.Saving
                  }
                  value={selectedYear?.id}
                  onChange={(value) => {
                    const v = Number(value);
                    const yearObj = divisionPricingYears.find(
                      (py) => py.id === v,
                    );
                    if (!yearObj || yearObj.isLocked) return;
                    userPickedPricingYearRef.current = true;
                    if (draftPricingYearIds.has(v)) {
                      navigate("/pricelist/draft", {
                        replace: true,
                        state: {
                          divisionId: getDivisionId(),
                          year: yearObj.year,
                          centreCode:
                            selectedCentreCode || readSelectedCentreCode(),
                        },
                      });
                      return;
                    }
                    lastAutoReloadKeyRef.current = null;
                    setSelectedYear(yearObj);
                    changeSelectedPricingYear(v);
                    reload(v);
                  }}
                  style={{ minWidth: 140, marginBottom: 16 }}
                  popupMatchSelectWidth
                >
                  {yearOptions.map((py) => (
                    <Option key={py.id} value={py.id} disabled={py.isLocked}>
                      {py.isLocked ? `${py.year} (Locked due to publishing...)` : py.year}
                    </Option>
                  ))}
                </Select>
              ) : (
                <Select
                  className="year-selector"
                  value={selectedYear?.id}
                  style={{ minWidth: 100, marginBottom: 16 }}
                  popupMatchSelectWidth
                  options={yearOptions.map((py) => ({
                    label: py.isLocked ? `${py.year} (Locked due to publishing...)` : String(py.year),
                    value: py.id,
                    disabled: py.isLocked,
                  }))}
                  onChange={(value) => {
                    const v = Number(value);
                    const yearObj = divisionPricingYears.find((py) => py.id === v);
                    if (!yearObj || yearObj.isLocked) return;
                    const centreCode =
                      selectedCentreCode || readSelectedCentreCode();
                    userPickedPricingYearRef.current = true;
                    lastAutoReloadKeyRef.current = null;
                    if (draftPricingYearIds.has(v)) {
                      setSelectedYear(yearObj);
                      changeSelectedPricingYear(v);
                      if (draftState?.year !== yearObj.year) {
                        navigate("/pricelist/draft", {
                          replace: true,
                          state: {
                            divisionId: getDivisionId(),
                            year: yearObj.year,
                            centreCode,
                          },
                        });
                      } else {
                        reload(v);
                      }
                      return;
                    }
                    changeSelectedPricingYear(v);
                    setSelectedYear(yearObj);
                    navigate("/pricelist", {
                      replace: true,
                      state: {
                        divisionId: getDivisionId(),
                        year: yearObj.year,
                        centreCode,
                      },
                    });
                  }}
                />
              )}
              {currenciesLoadError ? (
                <Text type="danger">
                  Failed to load currencies: {currenciesLoadError}
                </Text>
              ) : null}
            </Space>
          </Col>
        </Row>

        {!props.isDraftMode &&
        selectedPricingYear &&
        draftPricingYearIds.has(selectedPricingYear) ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="Unpublished draft pricing year"
            description={
              <>
                This pricing year is still in draft. Open the{" "}
                <Link
                  to="/pricelist/draft"
                  state={{
                    divisionId: getDivisionId(),
                    year: selectedYear?.year,
                    centreCode: selectedCentreCode || readSelectedCentreCode(),
                  }}
                >
                  Draft Pricing Year
                </Link>{" "}
                screen to view and edit unpublished changes.
              </>
            }
          />
        ) : null}

        {isCentreInactive && props.selectedCentreCode ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            title="Centre is inactive"
            description={
              <>
                You can view offerings for the Pricelist. To edit activate Centre{" "}
                <Link to="/centre-manager">here</Link>.
              </>
            }
          />
        ) : null}

        {selectedSchoolId ? (
          <Affix offsetTop={47}>
            <div className="top-fixed-row" style={{ marginBottom: "10px" }}>
              <Row>
                <Col span={24} style={{ textAlign: "right" }}>
                  <Space>
                    {props.isDraftMode && isViewingDraftYear ? (
                      <>
                        <Button
                          type="primary"
                          onClick={() => handleSave().then(() => message.success("Draft saved."))}
                          loading={isSaving}
                          disabled={props.isLoading || isCentreInactive}
                        >
                          Save
                        </Button>
                        <Button
                          onClick={handleDraftValidate}
                          loading={isValidatingDraft}
                          disabled={props.isLoading || isCentreInactive}
                        >
                          Validate
                        </Button>
                        <Button
                          type="primary"
                          onClick={handleDraftPublish}
                          loading={isPublishingDraft}
                          disabled={
                            isCentreInactive ||
                            !lastDraftValidation?.canPublish ||
                            isValidatingDraft ||
                            isPublishingDraft
                          }
                        >
                          Publish
                        </Button>
                      </>
                    ) : null}
                    <StatusIcon
                      status={status}
                      lastEventReceived={lastEventReceived}
                      unpublishedChanges={
                          Array.isArray(props.draftPricelist) &&
                          props.draftPricelist.some(
                              (x) => x?.hasPendingChanges === true,
                          )
                      }
                      onManualRefresh={refreshGridEvent}
                    />
                    <Space>
                      <Text type="secondary">Show All</Text>
                      <Switch
                          checked={showInactiveOnly}
                          onChange={setShowInactiveOnly}
                          disabled={props.isLoading}
                      />
                    </Space>
                    <TableActionButtons
                      canFilter={false}
                      canExport={!props.isLoading}
                      canEdit={
                          !isCentreInactive &&
                          !props.isLoading &&
                          props.user.permissionList.includes(UserPermissions.EDIT)
                      }
                      canAdd={!isCentreInactive && !props.isLoading}
                      editMode={editMode && !isCentreInactive}
                      formState={props.formState}
                      validationHint={
                        props.formState === SaveState.ValidationError
                          ? validationHint
                          : undefined
                      }
                      onEditClick={() => {
                        setEditMode(true);
                        props.setFormState(SaveState.NoChanges);
                      }}
                      onEditCancel={handleCancelEdit}
                      onEditSave={handleSave}
                      onAddClick={() => {
                        if (!editMode) {
                          setEditMode(true);
                          props.setFormState(SaveState.NoChanges);
                        }

                        if (props.selectedProductType === "Fee") {
                          feeTableRef.current?.beginAdd();
                        } else {
                          setAddOfferingModalVisible(true);
                        }
                      }}
                      isSaving={isSaving}
                      dataList={productOfferingsToCSV()}
                      csvFileName={`${DateHelper.ToDateString(
                        new Date(),
                        "yyyy.MM.dd",
                      )}_${centreName}_Pricelist_${
                        selectedPricingYearObj?.year ?? ""
                      }_${props.selectedProductType}_Export`}
                    />
                    <Dropdown
                      menu={{ items: currencyMenuItems }}
                      trigger={["click"]}
                    >
                      <Button
                        type="default"
                        icon={<EllipsisOutlined />}
                        aria-label="More actions"
                      />
                    </Dropdown>
                  </Space>
                </Col>
              </Row>
            </div>
          </Affix>
        ) : null}

        <div style={{ marginBottom: 12 }}>
          <PricelistFooter />
        </div>

        <Row>
          <Col span={24}>
            {props.selectedProductType === "Fee" ? (
              <FeePricelistTable
                ref={feeTableRef}
                editMode={editMode && !isCentreInactive}
                selectedSchoolId={selectedSchoolId}
                showInactiveOnly={showInactiveOnly}
                enabledCurrencies={enabledCurrencies}
                centreSchools={centreSchoolOptions}
                allowedStartDate={productAllowedStartDate}
                allowedEndDate={productAllowedEndDate}
                rowHighlightColors={props.isDraftMode && isViewingDraftYear ? rowHighlightColors : undefined}
                onRowHighlightChange={props.isDraftMode && isViewingDraftYear ? handleRowHighlightChange : undefined}
                reloadPricelist={() => {
                  if (selectedPricingYear) {
                    reload(selectedPricingYear);
                  }
                }}
              />
            ) : (
              <ProductTable
                ref={productTableRef}
                isDraftPricelist={props.isDraftMode && isViewingDraftYear}
                editMode={editMode && !isCentreInactive}
                pricingYearsOverride={divisionPricingYears}
                selectedSchoolId={selectedSchoolId}
                showInactiveOnly={showInactiveOnly}
                queueSchoolOp={queueSchoolOp}
                rowHighlightColors={props.isDraftMode && isViewingDraftYear ? rowHighlightColors : undefined}
                onRowHighlightChange={props.isDraftMode && isViewingDraftYear ? handleRowHighlightChange : undefined}
                allowedStartDate={productAllowedStartDate}
                allowedEndDate={productAllowedEndDate}
                reloadPricelist={() => {
                  if (selectedPricingYear) {
                    reload(selectedPricingYear);
                  }
                }}
              />
            )}
          </Col>
        </Row>

        <UnsavedChangesModal
          open={!!unsavedDialog}
          onLeave={confirmLeave}
          onStay={cancelLeave}
        />

        <AddProductOfferingModal
          visible={addOfferingModalVisible}
          onCancel={() => setAddOfferingModalVisible(false)}
          onComplete={(offering) => {
            const updatedDraft = [...(props.draftPricelist ?? []), offering];
            props.loadDraftPricelist(updatedDraft);
            props.setFormState(SaveState.PendingChanges);
            setAddOfferingModalVisible(false);
          }}
          selectedProductType={props.selectedProductType}
          selectedPricingYear={props.selectedPricingYear}
          selectedCentreCode={props.selectedCentreCode}
          centres={props.centres}
          centreSchools={centreSchoolOptions}
          pricingYears={divisionPricingYears}
          draftPricelist={props.draftPricelist}
          user={props.user}
          enabledCurrencies={enabledCurrencies}
        />

        <AddCurrencyModal
          open={addCurrencyModalVisible}
          onCancel={() => setAddCurrencyModalVisible(false)}
          onApply={(newIsos) => {
            const updated = syncCurrenciesInAllBands(
              props.draftPricelist ?? [],
              newIsos,
              selectedYear?.year,
            );
            props.loadDraftPricelist(updated);
            props.setFormState(SaveState.PendingChanges);
            setAddCurrencyModalVisible(false);
          }}
          centreCurrencyIso={centreCurrencyIso}
          enabledCurrencies={enabledCurrencies}
          currencies={allCurrencies ?? []}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (
  state: any,
  ownProps: MainContentProps,
): StateProps => ({
  schools: getSchools(state),
  centres: getCentres(state),
  products: getProducts(state),
  pricingYears: getPricingYears(state),

  pricelist: getPricelist(state),
  draftPricelist: getDraftPricelist(state),

  selectedCentreCode: getSelectedCentre(state),
  selectedProductType: getSelectedProductType(state),
  selectedPricingYear: getSelectedPricingYear(state),

  formState: getFormState(state),
  formErrors: getFormErrors(state),
  isLoading: getIsLoading(state),

  user: getCurrentUser(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  changeSelectedPricingYear: (pricingYear: number) =>
    dispatch(changeSelectedPricingYear(pricingYear)),
  changeSelectedCentre: (centreCode: string) =>
    dispatch(changeSelectedCentre(centreCode)),
  loadPricelist: (pricelist: EditPricelistResponse[]) =>
    dispatch(loadPricelist(pricelist)),
  loadDraftPricelist: (draftPricelist: EditPricelistResponse[]) =>
    dispatch(loadDraftPricelist(draftPricelist)),
  updateDraftPricelistItem: (
    pricelistItem: EditPricelistResponse,
    pricelistIndex: number
  ) => dispatch(updateDraftPricelistItem(pricelistItem, pricelistIndex)),
  setFormState: (formState: SaveState, errorMessage?: string) =>
    dispatch(setFormState(formState, errorMessage)),
  setIsLoading: (isLoading: boolean) => dispatch(setIsLoading(isLoading)),
  setFormErrors: (formErrors: FormError[]) =>
    dispatch(setFormErrors(formErrors)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainContentView);
